// Утилиты двухфакторной аутентификации (6.1) — чистые функции без React,
// покрыты node --test (twoFactor.test.ts).

/** Что ввёл пользователь в поле второго фактора. */
export type OtpInput =
  | { kind: 'totp'; value: string }
  | { kind: 'backup'; value: string }
  | { kind: 'invalid'; value: string }

/**
 * Нормализует ввод кода: 6 цифр из приложения (пробелы допустимы: «123 456»)
 * или резервный код XXXXX-XXXXX (регистр и пробелы не важны, дефис можно
 * не ставить). Сервер нормализует так же — здесь только для валидации кнопки
 * и аккуратной отправки.
 */
export function classifyOtpInput(raw: string): OtpInput {
  const compact = raw.replace(/\s+/g, '').toUpperCase()
  if (/^\d{6}$/.test(compact)) return { kind: 'totp', value: compact }
  const alnum = compact.replace(/-/g, '')
  if (/^[0-9A-Z]{10}$/.test(alnum)) {
    return { kind: 'backup', value: `${alnum.slice(0, 5)}-${alnum.slice(5)}` }
  }
  return { kind: 'invalid', value: compact }
}

/** Можно ли отправлять поле кода (вход/отключение/перевыпуск). */
export function isOtpSubmittable(raw: string): boolean {
  return classifyOtpInput(raw).kind !== 'invalid'
}

/** Только 6 цифр из приложения — для подтверждения настройки 2FA резервный код не подходит. */
export function isTotpCode(raw: string): boolean {
  return classifyOtpInput(raw).kind === 'totp'
}

/**
 * Текст резервных кодов для копирования / сохранения в файл.
 * Нумерация помогает вычёркивать использованные на распечатке.
 */
export function formatBackupCodes(codes: string[], account: string, issuedAt: Date = new Date()): string {
  const date = issuedAt.toISOString().slice(0, 10)
  const lines = codes.map((c, i) => `${String(i + 1).padStart(2, ' ')}. ${c}`)
  return [
    `EdPad Учитель — резервные коды 2FA`,
    `Учётная запись: ${account}`,
    `Выпущены: ${date}`,
    `Каждый код действует один раз. Храните в надёжном месте.`,
    '',
    ...lines,
    '',
  ].join('\n')
}

/** Имя файла для скачивания кодов (без ПДн — email в имени не нужен). */
export function backupCodesFileName(issuedAt: Date = new Date()): string {
  return `edpad-backup-codes-${issuedAt.toISOString().slice(0, 10)}.txt`
}

/**
 * Секрет base32 группами по 4 символа — так его проще перепечатать вручную
 * в приложение-аутентификатор, если QR не сканируется.
 */
export function groupSecret(secret: string): string {
  return secret.replace(/\s+/g, '').toUpperCase().replace(/(.{4})(?=.)/g, '$1 ')
}

/** Ответ API 403 `mfa_setup_required`: токен разрешает только настройку 2FA. */
export function isMfaSetupRequired(status: number, body: unknown): boolean {
  return status === 403 && typeof body === 'object' && body !== null &&
    (body as { error?: unknown }).error === 'mfa_setup_required'
}

/** Сколько дней осталось до истечения доверия к устройству (не меньше 0, округление вверх). */
export function trustedDaysLeft(expiresAt: number, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((expiresAt - now) / 86_400_000))
}

/**
 * Права на сброс 2FA сотруднику в админке школы — зеркало серверной проверки
 * (TwoFactorAdminService): директор/админ — любому, кроме себя; завуч — только
 * учителям. Сервер всё равно перепроверяет.
 */
export function canResetTwoFactor(actorRole: string, actorId: string, targetRole: string, targetId: string): boolean {
  if (actorId === targetId) return false
  if (actorRole === 'SUPER_ADMIN') return true
  if (actorRole === 'PRINCIPAL' || actorRole === 'ADMIN' || actorRole === 'IT_ADMIN') return targetRole !== 'SUPER_ADMIN'
  if (actorRole === 'VICE_PRINCIPAL') return targetRole === 'TEACHER'
  return false
}
